/**
 * CourseLoadErrorScreen — boot-gate retry surface for DCOURSE-007.
 *
 * Rendered by `AppNavigator` when `courseStore.loadError !== null` and
 * `loaded === false` — i.e. the user just installed the app, has no
 * cached course data, and the first `GET /api/courses` failed (or
 * returned empty). This screen is the ONLY thing the user can see in
 * that state: no nav, no tabs, no chat, no profile. Tapping Retry
 * fires `courseStore.load()` again; on success the screen unmounts
 * naturally because the gate condition flips.
 *
 * Why a dedicated screen instead of a banner/modal:
 *   - The app has nothing to render under the banner. Without courses,
 *     onboarding can't pick a target language, the chat needs source/
 *     target to build a session, the language pickers have nothing to
 *     show. So the surface is exclusive, not transient.
 *   - A retry-screen contract is easier for QA + the E2E story to
 *     assert against than "the splash should stay up and the banner
 *     should show" — one surface, one CTA, one happy path.
 *
 * Out of scope (per spec):
 *   - No "Continue with seed data" fallback button. The hard-gate is
 *     intentional — see the policy-change note in
 *     `docs/specs/mobile/dynamic-courses.mobile.md`.
 *   - No auto-retry / backoff. The user controls the cadence.
 *   - No support email or contact link (yet) — keep this screen
 *     minimal until the product team has copy for a support workflow.
 *
 * testIDs follow `mobile/ACCESSIBILITY.md` — `course-load-error-{element}`.
 */

import React, { useCallback } from 'react';
import { View, ActivityIndicator, Pressable } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { FS_LG, FS_MD, FS_SM } from '../theme';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useCourseStore } from '../store/courseStore';

export const CourseLoadErrorScreen = React.memo(function CourseLoadErrorScreen() {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  // Individual selectors — see CHAT_PERFORMANCE.md "Zustand selectors
  // must be individual" rule. Selecting `{loadError, loading, load}`
  // as one object would create a new ref every render and re-trigger
  // child renders unnecessarily.
  const loadError = useCourseStore((s) => s.loadError);
  const loading = useCourseStore((s) => s.loading);
  const load = useCourseStore((s) => s.load);

  const onRetry = useCallback(() => {
    // load() itself short-circuits concurrent calls via the `loading`
    // flag at the top of the body, but we also gate the button below
    // so the user never sees a double-fire from a fast double-tap.
    if (loading) return;
    void load();
  }, [load, loading]);

  // Render the error code line ONLY when we have something operator-
  // useful. `undefined` codes (transport failure with no body) would
  // otherwise show an empty badge that looks broken.
  const code = loadError?.code;

  return (
    <View
      testID="course-load-error-screen"
      accessibilityRole="alert"
      style={{
        flex: 1,
        backgroundColor: theme.background,
        paddingHorizontal: 24,
        paddingVertical: 48,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text
        testID="course-load-error-title"
        allowFontScaling={false}
        accessibilityRole="header"
        style={{
          fontSize: FS_LG * fontScale,
          fontWeight: '600',
          color: theme.text,
          textAlign: 'center',
          marginBottom: 12,
          ...ffs,
        }}
      >
        {t('courseLoadError.title')}
      </Text>

      <Text
        testID="course-load-error-body"
        allowFontScaling={false}
        style={{
          fontSize: FS_MD * fontScale,
          color: theme.textSecondary,
          textAlign: 'center',
          marginBottom: 24,
          maxWidth: 360,
          ...ffs,
        }}
      >
        {t('courseLoadError.body')}
      </Text>

      {code !== undefined && (
        <Text
          testID="course-load-error-code"
          allowFontScaling={false}
          // Small label so the diagnostic visually separates from the
          // body copy. Not localised — error codes are stable
          // identifiers, not user-facing language. Reuses `textSecondary`
          // (the only available muted tier in the theme today) — if
          // future iterations want a quieter tertiary tone, add a
          // `textTertiary` to the theme palette and switch here.
          style={{
            fontSize: FS_SM * fontScale,
            color: theme.textSecondary,
            textAlign: 'center',
            marginBottom: 24,
            letterSpacing: 0.4,
            ...ffs,
          }}
        >
          {t('courseLoadError.errorPrefix')}: {code}
        </Text>
      )}

      <Pressable
        testID="course-load-error-retry"
        accessibilityRole="button"
        accessibilityLabel={t('courseLoadError.retry')}
        accessibilityState={{ disabled: loading, busy: loading }}
        disabled={loading}
        onPress={onRetry}
        style={({ pressed }) => ({
          minWidth: 200,
          paddingVertical: 14,
          paddingHorizontal: 28,
          borderRadius: 12,
          // Tint the button neutral when the in-flight retry is running
          // so the disabled state reads as "we heard you, working on
          // it" rather than "broken / unresponsive". `primary` is the
          // brand action color from the theme palette; `textSecondary`
          // is the muted-text tier we reuse for the pending tint.
          backgroundColor: loading ? theme.textSecondary : theme.primary,
          opacity: pressed ? 0.85 : 1,
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'row',
          gap: 8,
        })}
      >
        {loading && (
          <ActivityIndicator
            size="small"
            color={theme.background}
            accessibilityLabel={t('courseLoadError.retrying')}
          />
        )}
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_MD * fontScale,
            fontWeight: '600',
            color: theme.background,
            ...ffs,
          }}
        >
          {loading ? t('courseLoadError.retrying') : t('courseLoadError.retry')}
        </Text>
      </Pressable>
    </View>
  );
});
